/**
 * Coups de pouce — Attendu 1065 « Repérer un nombre décimal sur une demi-droite
 * graduée ».
 *
 * abscisse_* (saisie, QCM) : entrée PARTAGÉE — trouver le pas, repérer l'entier
 * juste avant, compter les graduations, abscisse = entier + rang × pas. L'exemple
 * (revue 2026-09-06) : M sur la 3e graduation après 4, unité partagée en 5,
 * 4 + 3 × 0,2 = 4,6 (et pas 4,3). SVG piloté par les flèches de la sheet
 * (data-etapes / data-caption).
 *
 * pas_graduation_saisie : le pas, la Ne graduation après un entier, la
 * graduation juste avant, le rang d'un nombre. L'exemple : 7 à 9 en quarts,
 * 7,75 / 8,75 / 8,5.
 *
 * encadrer_* (QCM, V/F) : entrée PARTAGÉE — un nombre tombe sur une graduation
 * si sa partie décimale est un nombre entier de pas, sinon on l'encadre.
 * L'exemple : 7 à 9 en cinquièmes, 8,4 tombe, 8,3 est entre 8,2 et 8,4.
 */

// Demi-droite de 4 à 6 partagée en 5 : x de 40 à 320
const XA = (v) => 40 + 140 * (v - 4);
const LIGNE_A = (y) => {
    let s = `<line x1="24" y1="${y}" x2="340" y2="${y}" stroke="#1e293b" stroke-width="1.6"/><polygon points="340,${y} 332,${y - 4} 332,${y + 4}" fill="#1e293b"/>`;
    for (let i = 0; i <= 10; i++) {
        const gros = i % 5 === 0; const x = XA(4 + i / 5).toFixed(1);
        s += `<line x1="${x}" y1="${y - (gros ? 9 : 5)}" x2="${x}" y2="${y + (gros ? 9 : 5)}" stroke="#1e293b" stroke-width="${gros ? 1.6 : 1}"/>`;
        if (gros) s += `<text x="${x}" y="${y + 22}" font-size="12" font-weight="700" fill="#1e293b" text-anchor="middle">${4 + i / 5}</text>`;
    }
    return s;
};

const SVG_ABSCISSE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 250" width="100%" style="max-width:380px;height:auto;display:block" role="img" data-etapes="3"
     aria-label="Lire l'abscisse du point M sur une demi-droite graduée de 4 à 6 dont l'unité est partagée en 5, en 3 étapes à faire défiler avec les flèches. Étape 1 : cinq petites graduations par unité, le pas est 0,2. Étape 2 : l'entier juste avant M est 4, on compte 3 petites graduations après 4. Étape 3 : abscisse de M égale 4 plus 3 fois 0,2 égale 4,6 ; ce n'est pas 4,3, le rang n'est pas le chiffre des dixièmes">
  <style>
    @keyframes cp1065-pop { from { opacity: 0; } to { opacity: 1; } }
    .cp1065-pop { opacity: 0; animation: cp1065-pop 0.5s ease forwards; }
  </style>
  <!-- sous-titres : un par étape (exclusifs, pilotés par la sheet) -->
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="1" data-caption="1" font-family="system-ui,sans-serif">Quelle est l'abscisse du point M ?</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="2" font-family="system-ui,sans-serif">L'entier juste avant, puis on compte</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#B45309" opacity="0" data-caption="3" font-family="system-ui,sans-serif">Abscisse = entier + rang × pas</text>

  <!-- ── La demi-droite et le pas (étape 1) ────────────────────────────────── -->
  <g data-etape="1" font-family="system-ui,sans-serif">
    ${LIGNE_A(110)}
    <circle cx="${XA(4.6).toFixed(1)}" cy="110" r="4.2" fill="#1E3A8A"/>
    <text x="${XA(4.6).toFixed(1)}" y="82" font-size="13" font-weight="800" fill="#1E3A8A" text-anchor="middle">M</text>
    <text x="180" y="44" font-size="11" font-weight="600" fill="#475569" text-anchor="middle">chaque unité est partagée en 5 : le pas est 0,2</text>
    <text x="180" y="59" font-size="10.5" font-weight="600" fill="#475569" text-anchor="middle">(5 × 0,2 = 1, on retombe bien sur l'entier suivant)</text>
  </g>

  <!-- ── L'entier juste avant, puis on compte (étape 2) ─────────────────── -->
  <g data-etape="2" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1065-pop">
      <circle cx="${XA(4)}" cy="132" r="10" fill="none" stroke="#1D9E75" stroke-width="2"/>
      ${[1, 2, 3].map((k) => `<text x="${XA(4 + k / 5).toFixed(1)}" y="96" font-size="10" font-weight="700" fill="#1D9E75" text-anchor="middle">${k}</text>`).join('')}
      <line x1="${XA(4)}" y1="110" x2="${XA(4.6).toFixed(1)}" y2="110" stroke="#1D9E75" stroke-width="3"/>
      <text x="8" y="165" font-size="11.5" font-weight="700" fill="#1D9E75">L'entier juste avant M est 4.</text>
      <text x="8" y="180" font-size="11.5" font-weight="700" fill="#1D9E75">Après 4, on compte 3 petites graduations jusqu'à M.</text>
    </g>
  </g>

  <!-- ── Le calcul et le piège (étape 3) ────────────────────────────────── -->
  <g data-etape="3" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1065-pop">
      <text x="8" y="205" font-size="11.5" font-weight="700" fill="#B45309">Abscisse de M = 4 + 3 × 0,2 = 4 + 0,6 = 4,6 ✓</text>
      <text x="8" y="222" font-size="11.5" font-weight="700" fill="#B45309">Pas 4,3 : le rang 3 n'est pas le chiffre des dixièmes ✗</text>
      <text x="180" y="246" font-size="12" font-weight="800" fill="#1E3A8A" text-anchor="middle">Le pas d'abord, l'entier ensuite, puis on compte.</text>
    </g>
  </g>

  <text x="180" y="246" font-size="11" font-weight="700" fill="#b45309" text-anchor="middle" opacity="0" data-caption="1" font-family="system-ui,sans-serif">↓ Touche les flèches pour avancer, étape par étape ↓</text>
</svg>`.trim();

const AIDE_ABSCISSE = {
    pages: [
        {
            titre: 'La règle',
            lignes: [
                '**Trouve le pas** : compte les petites graduations entre deux entiers. En 2, chaque graduation vaut 0,5 ; en 4, elle vaut 0,25 ; en 5, 0,2 ; en 10, 0,1.',
                "**Repère l'entier juste avant le point**, puis compte les petites graduations après cet entier jusqu'au point.",
                '**Abscisse = entier + nombre de graduations × pas.** Le rang de la graduation n\'est pas le chiffre des dixièmes : la 3e graduation en cinquièmes vaut 0,6, pas 0,3.',
            ],
        },
        { titre: 'Un exemple', svg: SVG_ABSCISSE },
    ],
};

// ─── Le pas de la graduation : demi-droite de 7 à 9 partagée en 4 ──────────
const XP = (v) => 40 + 140 * (v - 7);
const LIGNE_P = (y) => {
    let s = `<line x1="24" y1="${y}" x2="340" y2="${y}" stroke="#1e293b" stroke-width="1.6"/><polygon points="340,${y} 332,${y - 4} 332,${y + 4}" fill="#1e293b"/>`;
    for (let i = 0; i <= 8; i++) {
        const gros = i % 4 === 0; const x = XP(7 + i / 4).toFixed(1);
        s += `<line x1="${x}" y1="${y - (gros ? 9 : 5)}" x2="${x}" y2="${y + (gros ? 9 : 5)}" stroke="#1e293b" stroke-width="${gros ? 1.6 : 1}"/>`;
        if (gros) s += `<text x="${x}" y="${y + 22}" font-size="12" font-weight="700" fill="#1e293b" text-anchor="middle">${7 + i / 4}</text>`;
    }
    return s;
};

const SVG_PAS = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 250" width="100%" style="max-width:380px;height:auto;display:block" role="img" data-etapes="3"
     aria-label="Le pas d'une demi-droite graduée de 7 à 9 dont l'unité est partagée en 4, en 3 étapes à faire défiler avec les flèches. Étape 1 : quatre petites graduations par unité, le pas est 0,25, et 4 fois 0,25 égale 1. Étape 2 : la 3e petite graduation après 7 repère 7 plus 3 fois 0,25 égale 7,75. Étape 3 : la graduation juste avant 9 repère 9 moins 0,25 égale 8,75 ; et 8,5, c'est 0,5 égale 2 fois 0,25, donc la 2e petite graduation après 8">
  <style>
    @keyframes cp1065-pop { from { opacity: 0; } to { opacity: 1; } }
    .cp1065-pop { opacity: 0; animation: cp1065-pop 0.5s ease forwards; }
  </style>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="1" data-caption="1" font-family="system-ui,sans-serif">Quel est le pas de cette graduation ?</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="2" font-family="system-ui,sans-serif">La 3e petite graduation après 7</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#B45309" opacity="0" data-caption="3" font-family="system-ui,sans-serif">Juste avant 9, et le rang de 8,5</text>

  <!-- ── La demi-droite et le pas (étape 1) ────────────────────────────────── -->
  <g data-etape="1" font-family="system-ui,sans-serif">
    ${LIGNE_P(110)}
    <text x="180" y="44" font-size="11" font-weight="600" fill="#475569" text-anchor="middle">chaque unité est partagée en 4 : le pas est 0,25</text>
    <text x="180" y="59" font-size="10.5" font-weight="600" fill="#475569" text-anchor="middle">(4 × 0,25 = 1, on retombe bien sur l'entier suivant)</text>
  </g>

  <!-- ── La 3e petite graduation après 7 (étape 2) ─────────────────────── -->
  <g data-etape="2" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1065-pop">
      ${[1, 2, 3].map((k) => `<text x="${XP(7 + k / 4).toFixed(1)}" y="96" font-size="10" font-weight="700" fill="#1D9E75" text-anchor="middle">${k}</text>`).join('')}
      <line x1="${XP(7)}" y1="110" x2="${XP(7.75).toFixed(1)}" y2="110" stroke="#1D9E75" stroke-width="3"/>
      <circle cx="${XP(7.75).toFixed(1)}" cy="110" r="4.2" fill="#1D9E75"/>
      <text x="${XP(7.75).toFixed(1)}" y="82" font-size="11.5" font-weight="800" fill="#1D9E75" text-anchor="middle">7,75</text>
      <text x="8" y="165" font-size="11.5" font-weight="700" fill="#1D9E75">La 3e petite graduation après 7 repère</text>
      <text x="8" y="180" font-size="11.5" font-weight="700" fill="#1D9E75">7 + 3 × 0,25 = 7 + 0,75 = 7,75 ✓</text>
    </g>
  </g>

  <!-- ── Juste avant 9, et le rang de 8,5 (étape 3) ─────────────────────── -->
  <g data-etape="3" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1065-pop">
      <circle cx="${XP(8.75).toFixed(1)}" cy="110" r="4.2" fill="#B45309"/>
      <text x="${XP(8.75).toFixed(1)}" y="82" font-size="11.5" font-weight="800" fill="#B45309" text-anchor="middle">8,75</text>
      <circle cx="${XP(8.5).toFixed(1)}" cy="110" r="4.2" fill="#B45309"/>
      <text x="${XP(8.5).toFixed(1)}" y="96" font-size="10" font-weight="700" fill="#B45309" text-anchor="middle">2e</text>
      <text x="8" y="205" font-size="11.5" font-weight="700" fill="#B45309">Juste avant 9 : 9 − 0,25 = 8,75.</text>
      <text x="8" y="222" font-size="11.5" font-weight="700" fill="#B45309">8,5 : 0,5 = 2 × 0,25, donc 2e petite graduation après 8.</text>
      <text x="180" y="246" font-size="12" font-weight="800" fill="#1E3A8A" text-anchor="middle">Tout part du pas : on l'ajoute, on le retire, on le compte.</text>
    </g>
  </g>

  <text x="180" y="246" font-size="11" font-weight="700" fill="#b45309" text-anchor="middle" opacity="0" data-caption="1" font-family="system-ui,sans-serif">↓ Touche les flèches pour avancer, étape par étape ↓</text>
</svg>`.trim();

const AIDE_PAS = {
    pages: [
        {
            titre: 'La règle',
            lignes: [
                '**Le pas** : compte les petites graduations entre deux entiers voisins. En 2, le pas est 0,5 ; en 4, 0,25 ; en 5, 0,2 ; en 10, 0,1. Vérifie : nombre de graduations × pas = 1.',
                "**La Ne petite graduation après un entier** repère l'entier + N × pas : la 3e après 7, avec un pas de 0,25, c'est 7 + 0,75 = 7,75.",
                "**La graduation juste avant un entier** repère l'entier moins un pas : juste avant 8, avec un pas de 0,25, c'est 7,75.",
                "**Pour trouver le rang d'un nombre**, regarde combien de pas font sa partie décimale : 7,5 avec un pas de 0,25, c'est 0,5 = 2 × 0,25, donc la 2e petite graduation après 7.",
            ],
        },
        { titre: 'Un exemple', svg: SVG_PAS },
    ],
};

// ─── Entre quelles graduations ? : demi-droite de 7 à 9 partagée en 5 ──────
const LIGNE_E = (y) => {
    let s = `<line x1="24" y1="${y}" x2="340" y2="${y}" stroke="#1e293b" stroke-width="1.6"/><polygon points="340,${y} 332,${y - 4} 332,${y + 4}" fill="#1e293b"/>`;
    for (let i = 0; i <= 10; i++) {
        const gros = i % 5 === 0; const x = XP(7 + i / 5).toFixed(1);
        s += `<line x1="${x}" y1="${y - (gros ? 9 : 5)}" x2="${x}" y2="${y + (gros ? 9 : 5)}" stroke="#1e293b" stroke-width="${gros ? 1.6 : 1}"/>`;
        if (gros) s += `<text x="${x}" y="${y + 22}" font-size="12" font-weight="700" fill="#1e293b" text-anchor="middle">${7 + i / 5}</text>`;
    }
    return s;
};

const SVG_ENCADRER = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 250" width="100%" style="max-width:380px;height:auto;display:block" role="img" data-etapes="3"
     aria-label="Un nombre tombe-t-il sur une graduation ? Demi-droite de 7 à 9 dont l'unité est partagée en 5, en 3 étapes à faire défiler avec les flèches. Étape 1 : cinq petites graduations par unité, le pas est 0,2. Étape 2 : 8,4 tombe sur la 2e petite graduation après 8, car 0,4 égale 2 fois 0,2. Étape 3 : 8,3 n'est pas un multiple de 0,2, il est entre 8,2 et 8,4, sur aucune graduation">
  <style>
    @keyframes cp1065-pop { from { opacity: 0; } to { opacity: 1; } }
    .cp1065-pop { opacity: 0; animation: cp1065-pop 0.5s ease forwards; }
  </style>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="1" data-caption="1" font-family="system-ui,sans-serif">8,4 et 8,3 tombent-ils sur une graduation ?</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="2" font-family="system-ui,sans-serif">8,4 : oui, 2e graduation après 8</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#B45309" opacity="0" data-caption="3" font-family="system-ui,sans-serif">8,3 : non, entre 8,2 et 8,4</text>

  <!-- ── La demi-droite et le pas (étape 1) ────────────────────────────────── -->
  <g data-etape="1" font-family="system-ui,sans-serif">
    ${LIGNE_E(110)}
    <text x="180" y="44" font-size="11" font-weight="600" fill="#475569" text-anchor="middle">chaque unité est partagée en 5 : le pas est 0,2</text>
    <text x="180" y="59" font-size="10.5" font-weight="600" fill="#475569" text-anchor="middle">les graduations après 8 : 8,2 ; 8,4 ; 8,6 ; 8,8</text>
  </g>

  <!-- ── 8,4 tombe sur une graduation (étape 2) ─────────────────────────── -->
  <g data-etape="2" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1065-pop">
      ${[1, 2].map((k) => `<text x="${XP(8 + k / 5).toFixed(1)}" y="96" font-size="10" font-weight="700" fill="#1D9E75" text-anchor="middle">${k}</text>`).join('')}
      <line x1="${XP(8)}" y1="110" x2="${XP(8.4).toFixed(1)}" y2="110" stroke="#1D9E75" stroke-width="3"/>
      <circle cx="${XP(8.4).toFixed(1)}" cy="110" r="4.2" fill="#1D9E75"/>
      <text x="${XP(8.4).toFixed(1)}" y="82" font-size="11.5" font-weight="800" fill="#1D9E75" text-anchor="middle">8,4</text>
      <text x="8" y="165" font-size="11.5" font-weight="700" fill="#1D9E75">8,4 : 0,4 = 2 × 0,2, un nombre entier de pas.</text>
      <text x="8" y="180" font-size="11.5" font-weight="700" fill="#1D9E75">Il tombe sur la 2e petite graduation après 8 ✓</text>
    </g>
  </g>

  <!-- ── 8,3 entre deux graduations (étape 3) ───────────────────────────── -->
  <g data-etape="3" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1065-pop">
      <circle cx="${XP(8.2).toFixed(1)}" cy="110" r="8" fill="none" stroke="#B45309" stroke-width="2"/>
      <circle cx="${XP(8.4).toFixed(1)}" cy="110" r="8" fill="none" stroke="#B45309" stroke-width="2"/>
      <circle cx="${XP(8.3).toFixed(1)}" cy="110" r="4.2" fill="#B45309"/>
      <text x="${XP(8.3).toFixed(1)}" y="150" font-size="11.5" font-weight="800" fill="#B45309" text-anchor="middle">8,3</text>
      <text x="8" y="205" font-size="11.5" font-weight="700" fill="#B45309">8,3 : 0,3 n'est pas un multiple de 0,2.</text>
      <text x="8" y="222" font-size="11.5" font-weight="700" fill="#B45309">Il est entre 8,2 et 8,4, sur aucune graduation ✗</text>
      <text x="180" y="246" font-size="12" font-weight="800" fill="#1E3A8A" text-anchor="middle">Le pas d'abord, puis on encadre.</text>
    </g>
  </g>

  <text x="180" y="246" font-size="11" font-weight="700" fill="#b45309" text-anchor="middle" opacity="0" data-caption="1" font-family="system-ui,sans-serif">↓ Touche les flèches pour avancer, étape par étape ↓</text>
</svg>`.trim();

const AIDE_ENCADRER = {
    pages: [
        {
            titre: 'La règle',
            lignes: [
                '**Trouve le pas** : compte les petites graduations entre deux entiers voisins. En 2, le pas est 0,5 ; en 4, 0,25 ; en 5, 0,2 ; en 10, 0,1.',
                '**Un nombre tombe sur une graduation** si sa partie décimale est un nombre entier de pas : avec un pas de 0,2, 8,4 tombe sur la 2e petite graduation après 8, car 0,4 = 2 × 0,2.',
                "**Sinon, il est entre deux graduations voisines** : 8,3 n'est pas un multiple de 0,2, il se situe entre 8,2 et 8,4.",
                '**Avec un pas de 0,1, tous les nombres à une décimale tombent sur une graduation.** Avec un pas de 0,2, seuls 0,2 ; 0,4 ; 0,6 et 0,8 y tombent ; avec 0,25, seuls 0,25 ; 0,5 et 0,75.',
                "**Pour comparer deux points**, le plus loin de l'origine est celui qui a la plus grande abscisse.",
            ],
        },
        { titre: 'Un exemple', svg: SVG_ENCADRER },
    ],
};

const CP_1065 = {
    abscisse_saisie: AIDE_ABSCISSE,
    abscisse_qcm_unique: AIDE_ABSCISSE,
    pas_graduation_saisie: AIDE_PAS,
    encadrer_qcm_unique: AIDE_ENCADRER,
    encadrer_tableau_vf: AIDE_ENCADRER,
};

export default CP_1065;
